<script setup>
import { ref, onMounted, computed } from 'vue';
import schedulingAPI from '../api/scheduling';

// Formatador de Data Local (corrige fuso horário e evita virar o dia antes da meia-noite)
const getLocalDateString = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Estado de Autenticação
const token = ref(localStorage.getItem('portal_prof_token') || '');
const professional = ref(JSON.parse(localStorage.getItem('portal_prof_data') || 'null'));

const loginEmail = ref('');
const loginPassword = ref('');
const isLoggingIn = ref(false);
const loginError = ref('');

// Modal de Troca de Senha
const showChangePasswordModal = ref(false);
const changePasswordForm = ref({
  current_password: '',
  new_password: '',
  new_password_confirmation: '',
});
const isChangingPassword = ref(false);
const changePasswordError = ref('');
const changePasswordSuccess = ref('');

const openChangePasswordModal = () => {
  changePasswordForm.value = {
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  };
  changePasswordError.value = '';
  changePasswordSuccess.value = '';
  showChangePasswordModal.value = true;
};

const submitChangePassword = async () => {
  if (!changePasswordForm.value.current_password || !changePasswordForm.value.new_password) {
    changePasswordError.value = 'Preencha a senha atual e a nova senha.';
    return;
  }
  if (changePasswordForm.value.new_password.length < 4) {
    changePasswordError.value = 'A nova senha deve ter no mínimo 4 dígitos.';
    return;
  }
  if (changePasswordForm.value.new_password !== changePasswordForm.value.new_password_confirmation) {
    changePasswordError.value = 'A confirmação da nova senha não confere.';
    return;
  }

  isChangingPassword.value = true;
  changePasswordError.value = '';
  changePasswordSuccess.value = '';

  try {
    const res = await schedulingAPI.portalChangePassword(token.value, changePasswordForm.value);
    changePasswordSuccess.value = res.data.message || 'Senha alterada com sucesso!';
    setTimeout(() => {
      showChangePasswordModal.value = false;
    }, 1500);
  } catch (err) {
    changePasswordError.value = err.response?.data?.error || 'Erro ao alterar senha.';
  } finally {
    isChangingPassword.value = false;
  }
};

// Modal de Esqueci Minha Senha
const showForgotPasswordModal = ref(false);
const forgotPasswordLogin = ref('');
const isSubmittingForgot = ref(false);
const forgotPasswordMessage = ref('');
const forgotPasswordError = ref('');

const openForgotPasswordModal = () => {
  forgotPasswordLogin.value = loginEmail.value || '';
  forgotPasswordMessage.value = '';
  forgotPasswordError.value = '';
  showForgotPasswordModal.value = true;
};

const submitForgotPassword = async () => {
  if (!forgotPasswordLogin.value.trim()) {
    forgotPasswordError.value = 'Informe seu e-mail ou WhatsApp cadastrado.';
    return;
  }

  isSubmittingForgot.value = true;
  forgotPasswordError.value = '';
  forgotPasswordMessage.value = '';

  try {
    const res = await schedulingAPI.portalForgotPassword(forgotPasswordLogin.value.trim());
    forgotPasswordMessage.value = res.data.message || 'Solicitação enviada com sucesso!';
  } catch (err) {
    forgotPasswordError.value = err.response?.data?.error || 'Profissional não encontrado.';
  } finally {
    isSubmittingForgot.value = false;
  }
};

// Estado da Aplicação
const activeTab = ref('agenda'); // 'agenda' | 'patients' | 'blocks'
const selectedDate = ref(getLocalDateString(new Date()));
const isLoadingAgenda = ref(false);
const agendaData = ref({ items: [], time_blocks: [], total_patients_count: 0, attended_count: 0 });

// Pacientes
const patientsList = ref([]);
const isLoadingPatients = ref(false);
const searchPatient = ref('');
const selectedPatientDetails = ref(null);
const isLoadingPatientDetails = ref(false);

// Bloqueios
const timeBlocksList = ref([]);
const isLoadingBlocks = ref(false);
const showNewBlockModal = ref(false);
const blockErrorMessage = ref('');
const isCreatingBlock = ref(false);
const newBlockData = ref({
  all_day: true,
  start_date: getLocalDateString(new Date()),
  end_date: getLocalDateString(new Date()),
  date: getLocalDateString(new Date()),
  start_time_slot: '08:00',
  end_time_slot: '12:00',
  reason: '',
});

const openNewBlockModal = () => {
  const curDate = selectedDate.value || getLocalDateString(new Date());
  blockErrorMessage.value = '';
  newBlockData.value = {
    all_day: true,
    start_date: curDate,
    end_date: curDate,
    date: curDate,
    start_time_slot: '08:00',
    end_time_slot: '12:00',
    reason: '',
  };
  showNewBlockModal.value = true;
};

// Modal de Prontuário / Evolução
const showEvolutionModal = ref(false);
const selectedItemForEvolution = ref(null);
const evolutionNotes = ref('');
const evolutionPainLevel = ref(0);
const isSavingEvolution = ref(false);

// Modal de Reagendamento
const showRescheduleModal = ref(false);
const selectedItemForReschedule = ref(null);
const rescheduleNewDate = ref('');
const rescheduleNewTime = ref('08:00');
const rescheduleNotes = ref('');
const rescheduleErrorMessage = ref('');
const isSubmittingReschedule = ref(false);

const openRescheduleModal = item => {
  selectedItemForReschedule.value = item;
  rescheduleNewDate.value = selectedDate.value;
  rescheduleNewTime.value = item.time_label || '08:00';
  rescheduleNotes.value = '';
  rescheduleErrorMessage.value = '';
  showRescheduleModal.value = true;
};

const submitReschedule = async () => {
  if (!rescheduleNewDate.value || !rescheduleNewTime.value) {
    rescheduleErrorMessage.value = 'Informe a nova data e horário.';
    return;
  }

  isSubmittingReschedule.value = true;
  rescheduleErrorMessage.value = '';

  try {
    const item = selectedItemForReschedule.value;
    const payload = {
      appointment_id: item.appointment_id || null,
      enrollment_id: item.enrollment_id || null,
      original_date: selectedDate.value,
      new_date: rescheduleNewDate.value,
      new_time_slot: rescheduleNewTime.value,
      notes: rescheduleNotes.value,
    };

    await schedulingAPI.portalReschedule(token.value, payload);
    showRescheduleModal.value = false;
    alert('Atendimento reagendado com sucesso!');
    await loadAgenda();
  } catch (err) {
    rescheduleErrorMessage.value =
      err.response?.data?.error ||
      err.response?.data?.errors?.join(', ') ||
      'Erro ao reagendar atendimento.';
  } finally {
    isSubmittingReschedule.value = false;
  }
};

// Formatador de Data
const formattedSelectedDate = computed(() => {
  if (!selectedDate.value) return '';
  const [y, m, d] = selectedDate.value.split('-');
  const dateObj = new Date(y, m - 1, d);
  const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' }).format(dateObj);
  return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${d}/${m}/${y}`;
});

const isToday = computed(() => {
  const todayStr = getLocalDateString(new Date());
  return selectedDate.value === todayStr;
});

// 1. Auth Flow
const handleLogin = async () => {
  if (!loginEmail.value.trim() || !loginPassword.value.trim()) {
    loginError.value = 'Preencha o e-mail e a senha de acesso.';
    return;
  }

  isLoggingIn.value = true;
  loginError.value = '';

  try {
    const res = await schedulingAPI.portalLogin(loginEmail.value.trim(), loginPassword.value);
    token.value = res.data.token;
    professional.value = res.data.professional;

    localStorage.setItem('portal_prof_token', token.value);
    localStorage.setItem('portal_prof_data', JSON.stringify(professional.value));

    await loadAgenda();
  } catch (err) {
    loginError.value = err.response?.data?.error || 'E-mail ou senha incorretos. Verifique com a administração.';
  } finally {
    isLoggingIn.value = false;
  }
};

const handleLogout = () => {
  token.value = '';
  professional.value = null;
  localStorage.removeItem('portal_prof_token');
  localStorage.removeItem('portal_prof_data');
};

// 2. Agenda Flow
const loadAgenda = async () => {
  if (!token.value) return;
  isLoadingAgenda.value = true;
  try {
    const res = await schedulingAPI.portalGetAgenda(token.value, selectedDate.value);
    agendaData.value = res.data || { items: [], time_blocks: [], total_patients_count: 0, attended_count: 0 };
  } catch (err) {
    if (err.response?.status === 401) {
      handleLogout();
    }
  } finally {
    isLoadingAgenda.value = false;
  }
};

const changeDate = daysOffset => {
  const [y, m, d] = selectedDate.value.split('-').map(Number);
  const dateObj = new Date(y, m - 1, d);
  dateObj.setDate(dateObj.getDate() + daysOffset);
  selectedDate.value = getLocalDateString(dateObj);
  loadAgenda();
};

const goToToday = () => {
  selectedDate.value = getLocalDateString(new Date());
  loadAgenda();
};

// 3. Evolução / Prontuário Flow
const openEvolutionModal = item => {
  selectedItemForEvolution.value = item;
  evolutionNotes.value = item.attendance?.notes || item.notes || '';
  evolutionPainLevel.value = item.attendance?.pain_level || 0;
  showEvolutionModal.value = true;
};

const saveEvolution = async (status = 'attended') => {
  if (!selectedItemForEvolution.value || !token.value) return;
  isSavingEvolution.value = true;

  try {
    const item = selectedItemForEvolution.value;
    const payload = {
      patient_id: item.patient?.id,
      session_date: selectedDate.value,
      notes: evolutionNotes.value,
      status: status,
      enrollment_id: item.enrollment_id || null,
      appointment_id: item.appointment_id || null,
    };

    if (item.attendance?.id) {
      await schedulingAPI.portalUpdateAttendance(token.value, item.attendance.id, {
        notes: evolutionNotes.value,
        status: status,
      });
    } else {
      await schedulingAPI.portalRecordAttendance(token.value, payload);
    }

    showEvolutionModal.value = false;
    await loadAgenda();
  } catch (err) {
    alert('Erro ao salvar prontuário. Tente novamente.');
  } finally {
    isSavingEvolution.value = false;
  }
};

// 4. Pacientes Flow
const loadPatients = async () => {
  if (!token.value) return;
  isLoadingPatients.value = true;
  try {
    const res = await schedulingAPI.portalGetPatients(token.value);
    patientsList.value = res.data || [];
  } catch (err) {
    console.error('Erro ao carregar pacientes:', err);
  } finally {
    isLoadingPatients.value = false;
  }
};

const filteredPatients = computed(() => {
  if (!searchPatient.value.trim()) return patientsList.value;
  const q = searchPatient.value.toLowerCase();
  return patientsList.value.filter(p =>
    p.name.toLowerCase().includes(q) || (p.phone_number && p.phone_number.includes(q))
  );
});

const openPatientDetails = async patientId => {
  if (!token.value) return;
  isLoadingPatientDetails.value = true;
  selectedPatientDetails.value = null;
  try {
    const res = await schedulingAPI.portalGetPatientDetails(token.value, patientId);
    selectedPatientDetails.value = res.data;
  } catch (err) {
    alert('Erro ao carregar histórico clínico do paciente.');
  } finally {
    isLoadingPatientDetails.value = false;
  }
};

// 5. Bloqueios / Folgas Flow
const loadBlocks = async () => {
  if (!token.value) return;
  isLoadingBlocks.value = true;
  try {
    const res = await schedulingAPI.portalGetTimeBlocks(token.value);
    timeBlocksList.value = res.data || [];
  } catch (err) {
    console.error('Erro ao carregar bloqueios:', err);
  } finally {
    isLoadingBlocks.value = false;
  }
};

const createBlock = async () => {
  if (newBlockData.value.all_day) {
    if (!newBlockData.value.start_date) {
      blockErrorMessage.value = 'Informe a data do bloqueio.';
      return;
    }
  } else {
    if (!newBlockData.value.date || !newBlockData.value.start_time_slot || !newBlockData.value.end_time_slot) {
      blockErrorMessage.value = 'Informe a data, o horário de início e o horário de término.';
      return;
    }
  }

  isCreatingBlock.value = true;
  blockErrorMessage.value = '';

  try {
    await schedulingAPI.portalCreateTimeBlock(token.value, newBlockData.value);
    showNewBlockModal.value = false;
    await loadBlocks();
    await loadAgenda();
  } catch (err) {
    blockErrorMessage.value =
      err.response?.data?.error ||
      err.response?.data?.errors?.join(', ') ||
      'Erro ao criar bloqueio de horário.';
  } finally {
    isCreatingBlock.value = false;
  }
};

const removeBlock = async id => {
  if (!confirm('Deseja remover este bloqueio e liberar o horário na agenda?')) return;
  try {
    await schedulingAPI.portalDeleteTimeBlock(token.value, id);
    await loadBlocks();
    await loadAgenda();
  } catch (err) {
    alert('Erro ao excluir bloqueio.');
  }
};

// WhatsApp Direct Link
const getWhatsAppLink = phone => {
  if (!phone) return '#';
  const cleanPhone = phone.replace(/\D/g, '');
  return `https://wa.me/${cleanPhone}`;
};

onMounted(async () => {
  if (token.value) {
    await loadAgenda();
  }
});
</script>

<template>
  <div class="min-h-screen bg-slate-950 text-slate-100 font-sans flex flex-col antialiased selection:bg-blue-600 selection:text-white">
    <!-- VIEW 1: TELA DE LOGIN (Caso não esteja autenticado) -->
    <div
      v-if="!token || !professional"
      class="flex-1 flex items-center justify-center p-4 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950"
    >
      <div class="w-full max-w-md bg-slate-900/90 border border-slate-800 rounded-3xl p-8 shadow-2xl backdrop-blur-md">
        <!-- Logo / Header -->
        <div class="text-center mb-8">
          <div class="w-16 h-16 bg-blue-600/20 border border-blue-500/30 rounded-2xl flex items-center justify-center text-blue-400 text-2xl font-bold mx-auto mb-4 shadow-lg shadow-blue-500/10">
            🩺
          </div>
          <h1 class="text-xl font-bold text-white tracking-tight">Portal do Profissional</h1>
          <p class="text-xs text-slate-400 mt-1">
            Acesse sua agenda clínica, prontuários e pacientes
          </p>
        </div>

        <!-- Form -->
        <form class="space-y-4" @submit.prevent="handleLogin">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">E-mail ou WhatsApp</label>
            <input
              v-model="loginEmail"
              type="text"
              placeholder="email@empresa.com ou (11) 99999-9999"
              required
              class="w-full h-11 px-3.5 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
            />
          </div>

          <div>
            <div class="flex items-center justify-between mb-1.5">
              <label class="block text-xs font-semibold text-slate-300">Senha</label>
              <button
                type="button"
                class="text-[11px] text-blue-400 hover:text-blue-300 font-medium cursor-pointer transition-colors"
                @click="openForgotPasswordModal"
              >
                Esqueci minha senha
              </button>
            </div>
            <input
              v-model="loginPassword"
              type="password"
              placeholder="••••••••"
              required
              class="w-full h-11 px-3.5 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
            />
          </div>

          <div v-if="loginError" class="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400 font-medium">
            {{ loginError }}
          </div>

          <button
            type="submit"
            class="w-full h-11 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 mt-2"
            :disabled="isLoggingIn"
          >
            <span v-if="isLoggingIn" class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
            <span>{{ isLoggingIn ? 'Entrando...' : 'Entrar no Portal' }}</span>
          </button>
        </form>
      </div>
    </div>

    <!-- VIEW 2: PORTAL DO PROFISSIONAL LOGADO -->
    <div v-else class="flex-1 flex flex-col max-w-4xl w-full mx-auto pb-20 sm:pb-8">
      <!-- Top Navbar Mobile First -->
      <header class="bg-slate-900/90 border-b border-slate-800 px-4 py-3.5 sticky top-0 z-30 backdrop-blur-md flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-bold text-sm">
            {{ professional.name?.charAt(0) || 'P' }}
          </div>
          <div>
            <h2 class="text-sm font-bold text-white leading-tight">{{ professional.name }}</h2>
            <span class="text-[11px] text-blue-400 font-medium">{{ professional.specialty || 'Profissional de Saúde' }}</span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            type="button"
            class="px-2.5 py-1.5 text-xs text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-750 border border-slate-700 rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 font-medium"
            title="Alterar Senha"
            @click="openChangePasswordModal"
          >
            <span>🔑</span>
            <span>Senha</span>
          </button>

          <button
            type="button"
            class="px-2.5 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer flex items-center gap-1 font-medium"
            title="Sair do Portal"
            @click="handleLogout"
          >
            <span>Sair</span>
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </header>

      <!-- Navigation Bar (Desktop & Mobile Tabs) -->
      <div class="px-4 pt-3 pb-1 border-b border-slate-800 flex items-center justify-between gap-1 overflow-x-auto">
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer"
            :class="activeTab === 'agenda' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-900'"
            @click="activeTab = 'agenda'; loadAgenda()"
          >
            <span>🗓️ Minha Agenda</span>
            <span v-if="agendaData.total_patients_count > 0" class="px-1.5 py-0.5 rounded-full text-[10px] bg-slate-950 text-blue-300">
              {{ agendaData.total_patients_count }}
            </span>
          </button>

          <button
            type="button"
            class="px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer"
            :class="activeTab === 'patients' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-900'"
            @click="activeTab = 'patients'; loadPatients()"
          >
            <span>👥 Meus Pacientes</span>
          </button>

          <button
            type="button"
            class="px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer"
            :class="activeTab === 'blocks' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-900'"
            @click="activeTab = 'blocks'; loadBlocks()"
          >
            <span>🔒 Folgas / Bloqueios</span>
          </button>
        </div>
      </div>

      <!-- MAIN CONTENT -->
      <main class="flex-1 p-4 space-y-4">
        <!-- ============================================== -->
        <!-- ABA 1: MINHA AGENDA DO DIA                      -->
        <!-- ============================================== -->
        <div v-if="activeTab === 'agenda'" class="space-y-4">
          <!-- Date Navigator -->
          <div class="p-3 bg-slate-900 border border-slate-800 rounded-2xl flex items-center justify-between gap-2 shadow-sm">
            <button
              type="button"
              class="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center justify-center cursor-pointer transition-colors"
              title="Dia Anterior"
              @click="changeDate(-1)"
            >
              ‹
            </button>

            <div class="text-center cursor-pointer" @click="goToToday">
              <span class="block text-xs font-bold text-white">{{ formattedSelectedDate }}</span>
              <span v-if="isToday" class="inline-block text-[10px] text-emerald-400 font-bold tracking-wide">
                ● Hoje
              </span>
              <span v-else class="inline-block text-[10px] text-blue-400 font-medium hover:underline">
                Ir para Hoje
              </span>
            </div>

            <button
              type="button"
              class="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center justify-center cursor-pointer transition-colors"
              title="Próximo Dia"
              @click="changeDate(1)"
            >
              ›
            </button>
          </div>

          <!-- Progress Header: Concluídos x Pendentes -->
          <div class="flex items-center justify-between text-xs px-2 text-slate-400">
            <span>
              Atendimentos: <strong class="text-white">{{ agendaData.items.length }}</strong>
            </span>
            <span class="text-emerald-400 font-medium">
              ✓ {{ agendaData.attended_count }} concluído(s)
            </span>
          </div>

          <!-- Time Blocks Alert (Se houver bloqueio no dia) -->
          <div v-if="agendaData.time_blocks && agendaData.time_blocks.length > 0" class="space-y-2">
            <div
              v-for="b in agendaData.time_blocks"
              :key="b.id"
              class="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-center gap-2"
            >
              <span>🛑</span>
              <span class="font-bold">{{ b.time_label }}:</span>
              <span>{{ b.reason }}</span>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="isLoadingAgenda" class="py-16 text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto"></div>
          </div>

          <!-- Empty Agenda -->
          <div
            v-else-if="agendaData.items.length === 0"
            class="p-12 text-center border border-dashed border-slate-800 rounded-3xl bg-slate-900/40"
          >
            <div class="text-3xl mb-2">☕</div>
            <h3 class="text-sm font-bold text-slate-200">Nenhum atendimento neste dia</h3>
            <p class="text-xs text-slate-500 mt-1">Você não possui pacientes marcados para esta data.</p>
          </div>

          <!-- Agenda List Cards -->
          <div v-else class="space-y-3">
            <div
              v-for="item in agendaData.items"
              :key="item.id"
              class="p-4 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-700 transition-all shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 relative overflow-hidden"
              :class="item.attended ? 'border-emerald-500/40 bg-emerald-950/10' : ''"
            >
              <!-- Color Indicator -->
              <div
                class="absolute left-0 top-0 bottom-0 w-1.5"
                :style="{ backgroundColor: item.service?.color || '#3B82F6' }"
              />

              <!-- Left Column: Hour & Patient -->
              <div class="pl-2 space-y-1">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-mono font-bold text-white bg-slate-950 px-2 py-0.5 rounded-md border border-slate-800">
                    {{ item.time_label }}
                  </span>
                  <span
                    class="text-[10px] px-2 py-0.5 rounded font-bold border"
                    :style="{
                      backgroundColor: `${item.service?.color || '#3B82F6'}20`,
                      borderColor: `${item.service?.color || '#3B82F6'}40`,
                      color: item.service?.color || '#93C5FD'
                    }"
                  >
                    {{ item.service?.name }}
                  </span>
                </div>

                <div class="flex items-center gap-2">
                  <h3 class="text-sm font-bold text-white">{{ item.patient?.name || 'Sem nome' }}</h3>
                  <a
                    v-if="item.patient?.phone_number"
                    :href="getWhatsAppLink(item.patient.phone_number)"
                    target="_blank"
                    class="text-[11px] text-emerald-400 hover:text-emerald-300 font-semibold inline-flex items-center gap-1 bg-emerald-950/50 border border-emerald-800/60 px-2 py-0.5 rounded-md"
                    title="Chamar no WhatsApp"
                  >
                    <span>💬 WhatsApp</span>
                  </a>
                </div>

                <p v-if="item.notes" class="text-xs text-slate-400 italic line-clamp-1">
                  "{{ item.notes }}"
                </p>
              </div>

              <!-- Right Column: Actions / Check-in -->
              <div class="flex items-center gap-2 shrink-0 sm:self-center pl-2 sm:pl-0">
                <button
                  type="button"
                  class="px-3 py-2 rounded-xl text-xs font-semibold text-slate-300 bg-slate-800 hover:bg-slate-700 hover:text-white transition-all flex items-center gap-1 cursor-pointer border border-slate-700/60"
                  title="Reagendar este atendimento para outra data/horário"
                  @click="openRescheduleModal(item)"
                >
                  <span>🔄</span>
                  <span>Reagendar</span>
                </button>

                <button
                  type="button"
                  class="px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
                  :class="item.attended ? 'bg-emerald-600/30 text-emerald-300 border border-emerald-500/50 hover:bg-emerald-600/40' : 'bg-blue-600 hover:bg-blue-500 text-white'"
                  @click="openEvolutionModal(item)"
                >
                  <span>{{ item.attended ? '✓ Prontuário Salvo' : '📝 Evoluir / Prontuário' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================== -->
        <!-- ABA 2: MEUS PACIENTES & PRONTUÁRIOS             -->
        <!-- ============================================== -->
        <div v-else-if="activeTab === 'patients'" class="space-y-4">
          <!-- Search Box -->
          <div class="relative">
            <svg
              class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              v-model="searchPatient"
              type="text"
              placeholder="Buscar paciente pelo nome ou telefone..."
              class="w-full h-10 pl-9.5 pr-8 text-xs bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-all"
            />
            <button
              v-if="searchPatient"
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white cursor-pointer p-0.5"
              @click="searchPatient = ''"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Loading -->
          <div v-if="isLoadingPatients" class="py-16 text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto"></div>
          </div>

          <!-- Empty Patients -->
          <div
            v-else-if="filteredPatients.length === 0"
            class="p-12 text-center border border-dashed border-slate-800 rounded-3xl bg-slate-900/40"
          >
            <h3 class="text-sm font-bold text-slate-300">Nenhum paciente encontrado</h3>
          </div>

          <!-- Patients List -->
          <div v-else class="space-y-3">
            <div
              v-for="p in filteredPatients"
              :key="p.id"
              class="p-4 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-700 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer"
              @click="openPatientDetails(p.id)"
            >
              <div>
                <h3 class="text-sm font-bold text-white">{{ p.name }}</h3>
                <div class="flex items-center gap-3 text-xs text-slate-400 mt-1">
                  <span v-if="p.phone_number">📱 {{ p.phone_number }}</span>
                  <span class="text-blue-400">⚡ {{ p.total_sessions_done }} sessões feitas</span>
                  <span v-if="p.last_session_date" class="text-slate-500">Última: {{ p.last_session_date }}</span>
                </div>
              </div>

              <button
                type="button"
                class="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl transition-colors self-start sm:self-center"
              >
                Ver Prontuário ➔
              </button>
            </div>
          </div>
        </div>

        <!-- ============================================== -->
        <!-- ABA 3: FOLGAS & BLOQUEIOS DE HORÁRIO            -->
        <!-- ============================================== -->
        <div v-else-if="activeTab === 'blocks'" class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-sm font-bold text-white">Minhas Folgas & Bloqueios</h3>
              <p class="text-xs text-slate-400">Horários bloqueados impedem agendamentos da IA e da recepção.</p>
            </div>

            <button
              type="button"
              class="px-3.5 py-2 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer"
              @click="openNewBlockModal"
            >
              + Adicionar Folga/Bloqueio
            </button>
          </div>

          <!-- Loading -->
          <div v-if="isLoadingBlocks" class="py-16 text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto"></div>
          </div>

          <!-- Empty Blocks -->
          <div
            v-else-if="timeBlocksList.length === 0"
            class="p-12 text-center border border-dashed border-slate-800 rounded-3xl bg-slate-900/40"
          >
            <h3 class="text-sm font-bold text-slate-300">Nenhum bloqueio agendado</h3>
            <p class="text-xs text-slate-500 mt-1">Sua agenda está com disponibilidade normal.</p>
          </div>

          <!-- Blocks List -->
          <div v-else class="space-y-2.5">
            <div
              v-for="b in timeBlocksList"
              :key="b.id"
              class="p-3.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-between gap-3"
            >
              <div class="flex items-center gap-2.5">
                <span class="text-base">🛑</span>
                <div>
                  <span class="text-xs font-bold text-amber-300 block">{{ b.time_label }}</span>
                  <span class="text-xs text-slate-400">{{ b.reason }}</span>
                </div>
              </div>

              <button
                type="button"
                class="text-xs text-red-400 hover:text-red-300 p-1.5 rounded-lg hover:bg-red-500/10 cursor-pointer transition-colors"
                title="Desbloquear horário"
                @click="removeBlock(b.id)"
              >
                ✕ Desbloquear
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- ============================================== -->
    <!-- MODAL 1: EVOLUÇÃO CLÍNICA & PRONTUÁRIO         -->
    <!-- ============================================== -->
    <div
      v-if="showEvolutionModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="showEvolutionModal = false"
    >
      <div class="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div class="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div>
            <h3 class="text-sm font-bold text-white">Evolução Clínica & Prontuário</h3>
            <p class="text-xs text-blue-400 font-medium">{{ selectedItemForEvolution?.patient?.name }}</p>
          </div>
          <button type="button" class="text-slate-400 hover:text-white cursor-pointer" @click="showEvolutionModal = false">✕</button>
        </div>

        <div class="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">
              Conduta Clínica / Evolução da Sessão <span class="text-red-400">*</span>
            </label>
            <textarea
              v-model="evolutionNotes"
              rows="5"
              placeholder="Descreva os exercícios realizados, queixas do paciente, manipulações, liberação miofascial e orientações..."
              class="w-full p-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>

          <!-- Escala Visual de Dor -->
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-xs font-semibold text-slate-300">Escala de Dor do Paciente (EVA)</label>
              <span class="text-xs font-bold text-amber-400 font-mono">{{ evolutionPainLevel }}/10</span>
            </div>
            <input
              v-model.number="evolutionPainLevel"
              type="range"
              min="0"
              max="10"
              class="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
          </div>
        </div>

        <div class="px-6 py-3.5 border-t border-slate-800 bg-slate-950 flex items-center justify-between gap-2">
          <button
            type="button"
            class="px-3.5 py-2 text-xs font-semibold text-red-400 hover:text-red-300 rounded-xl cursor-pointer"
            @click="saveEvolution('no_show')"
          >
            ❌ Marcar Falta
          </button>

          <div class="flex items-center gap-2">
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white cursor-pointer"
              @click="showEvolutionModal = false"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50"
              :disabled="isSavingEvolution"
              @click="saveEvolution('attended')"
            >
              {{ isSavingEvolution ? 'Salvando...' : '✓ Salvar Prontuário' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- MODAL 2: DETALHES COMPLETOS DO PACIENTE        -->
    <!-- ============================================== -->
    <div
      v-if="selectedPatientDetails"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="selectedPatientDetails = null"
    >
      <div class="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div class="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div>
            <h3 class="text-base font-bold text-white">{{ selectedPatientDetails.patient?.name }}</h3>
            <p class="text-xs text-slate-400">{{ selectedPatientDetails.patient?.phone_number }} • {{ selectedPatientDetails.patient?.email }}</p>
          </div>
          <button type="button" class="text-slate-400 hover:text-white cursor-pointer" @click="selectedPatientDetails = null">✕</button>
        </div>

        <div class="p-6 space-y-5 overflow-y-auto flex-1">
          <!-- Queixa Principal / Histórico -->
          <div v-if="selectedPatientDetails.patient?.medical_history" class="p-3.5 bg-slate-800/80 border border-slate-700 rounded-2xl">
            <h4 class="text-xs font-bold text-blue-300 uppercase tracking-wide mb-1">Histórico Clínico / Queixa Inicial</h4>
            <p class="text-xs text-slate-300">{{ selectedPatientDetails.patient.medical_history }}</p>
          </div>

          <!-- Histórico de Evoluções Clínicas -->
          <div>
            <h4 class="text-xs font-bold text-slate-200 uppercase tracking-wide mb-2.5">Histórico de Atendimentos Realizados</h4>
            <div v-if="selectedPatientDetails.clinical_records?.length === 0" class="p-6 text-center text-xs text-slate-500 border border-dashed border-slate-800 rounded-2xl">
              Nenhuma evolução clínica registrada ainda para este paciente.
            </div>
            <div v-else class="space-y-2.5">
              <div
                v-for="rec in selectedPatientDetails.clinical_records"
                :key="rec.id"
                class="p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800 text-xs space-y-1.5"
              >
                <div class="flex items-center justify-between">
                  <span class="font-bold text-white">{{ rec.date }}</span>
                  <span class="text-[10px] px-2 py-0.5 rounded-md font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    {{ rec.service_name }}
                  </span>
                </div>
                <p class="text-slate-300 leading-relaxed">{{ rec.notes }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- MODAL 3: NOVO BLOQUEIO DE HORÁRIO / FOLGA       -->
    <!-- ============================================== -->
    <div
      v-if="showNewBlockModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="showNewBlockModal = false"
    >
      <div class="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        <div class="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <h3 class="text-sm font-bold text-white">Adicionar Bloqueio / Folga</h3>
          <button type="button" class="text-slate-400 hover:text-white cursor-pointer" @click="showNewBlockModal = false">✕</button>
        </div>

        <form class="p-6 space-y-4" @submit.prevent="createBlock">
          <!-- Alerta de Conflito com Atendimentos Existentes -->
          <div v-if="blockErrorMessage" class="p-3.5 bg-red-500/10 border border-red-500/30 rounded-2xl text-xs text-red-300 space-y-1 leading-relaxed">
            <div class="font-bold text-red-400 flex items-center gap-1.5">
              <span>⚠️</span>
              <span>Não foi possível bloquear</span>
            </div>
            <p>{{ blockErrorMessage }}</p>
          </div>

          <!-- Switch / Toggle Dia Inteiro -->
          <div class="flex items-center justify-between p-3.5 bg-slate-800/80 border border-slate-700/80 rounded-2xl">
            <div>
              <span class="text-xs font-bold text-white block">Bloquear o dia todo</span>
              <span class="text-[11px] text-slate-400">Folga de dia inteiro ou recesso</span>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input v-model="newBlockData.all_day" type="checkbox" class="sr-only peer" />
              <div class="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-600"></div>
            </label>
          </div>

          <!-- Campos para Dia Inteiro (ou Período) -->
          <div v-if="newBlockData.all_day" class="space-y-2">
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-slate-300 mb-1">Data Início <span class="text-red-400">*</span></label>
                <input
                  v-model="newBlockData.start_date"
                  type="date"
                  required
                  class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-300 mb-1">Data Fim <span class="text-red-400">*</span></label>
                <input
                  v-model="newBlockData.end_date"
                  type="date"
                  required
                  class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <p class="text-[11px] text-slate-500 italic">Para apenas 1 dia de folga, mantenha a Data Fim igual à Data Início.</p>
          </div>

          <!-- Campos para Horário Parcial Específico -->
          <div v-else class="space-y-3">
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">Data do Bloqueio <span class="text-red-400">*</span></label>
              <input
                v-model="newBlockData.date"
                type="date"
                required
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-slate-300 mb-1">Horário Início <span class="text-red-400">*</span></label>
                <input
                  v-model="newBlockData.start_time_slot"
                  type="time"
                  required
                  class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-slate-300 mb-1">Horário Fim <span class="text-red-400">*</span></label>
                <input
                  v-model="newBlockData.end_time_slot"
                  type="time"
                  required
                  class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1">Motivo do Bloqueio</label>
            <input
              v-model="newBlockData.reason"
              type="text"
              placeholder="Ex: Consulta médica, Folga, Congresso..."
              class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div class="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white cursor-pointer"
              @click="showNewBlockModal = false"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-5 py-2 text-xs font-bold text-white bg-amber-600 hover:bg-amber-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50"
              :disabled="isCreatingBlock"
            >
              {{ isCreatingBlock ? 'Verificando...' : 'Criar Bloqueio' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- MODAL 4: REAGENDAR ATENDIMENTO                 -->
    <!-- ============================================== -->
    <div
      v-if="showRescheduleModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="showRescheduleModal = false"
    >
      <div class="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        <div class="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div>
            <h3 class="text-sm font-bold text-white">Reagendar Atendimento</h3>
            <p class="text-xs text-blue-400 font-medium">{{ selectedItemForReschedule?.patient?.name }}</p>
          </div>
          <button type="button" class="text-slate-400 hover:text-white cursor-pointer" @click="showRescheduleModal = false">✕</button>
        </div>

        <form class="p-6 space-y-4" @submit.prevent="submitReschedule">
          <!-- Alerta de Erro / Conflito -->
          <div v-if="rescheduleErrorMessage" class="p-3.5 bg-red-500/10 border border-red-500/30 rounded-2xl text-xs text-red-300 space-y-1 leading-relaxed">
            <div class="font-bold text-red-400 flex items-center gap-1.5">
              <span>⚠️</span>
              <span>Não foi possível reagendar</span>
            </div>
            <p>{{ rescheduleErrorMessage }}</p>
          </div>

          <div class="p-3.5 bg-slate-800/60 border border-slate-700/60 rounded-2xl text-xs space-y-1">
            <div class="text-slate-400">Atendimento atual:</div>
            <div class="font-bold text-white">{{ selectedItemForReschedule?.service?.name }} • {{ selectedItemForReschedule?.time_label }}</div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">Nova Data <span class="text-red-400">*</span></label>
              <input
                v-model="rescheduleNewDate"
                type="date"
                required
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">Novo Horário <span class="text-red-400">*</span></label>
              <input
                v-model="rescheduleNewTime"
                type="time"
                required
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1">Motivo do Reagendamento</label>
            <input
              v-model="rescheduleNotes"
              type="text"
              placeholder="Ex: Remarcação solicitada pelo paciente"
              class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-blue-500"
            />
          </div>

          <div class="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white cursor-pointer"
              @click="showRescheduleModal = false"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50"
              :disabled="isSubmittingReschedule"
            >
              {{ isSubmittingReschedule ? 'Reagendando...' : 'Confirmar Reagendamento' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- MODAL 5: ALTERAR SENHA DO PROFISSIONAL        -->
    <!-- ============================================== -->
    <div
      v-if="showChangePasswordModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="showChangePasswordModal = false"
    >
      <div class="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        <div class="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
              🔑
            </div>
            <div>
              <h3 class="text-sm font-bold text-white">Alterar Minha Senha</h3>
              <p class="text-xs text-slate-400">Defina uma nova senha para acessar o portal.</p>
            </div>
          </div>
          <button type="button" class="text-slate-400 hover:text-white cursor-pointer" @click="showChangePasswordModal = false">✕</button>
        </div>

        <form class="p-6 space-y-4" @submit.prevent="submitChangePassword">
          <div v-if="changePasswordError" class="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400 font-medium">
            {{ changePasswordError }}
          </div>

          <div v-if="changePasswordSuccess" class="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-xs text-emerald-400 font-medium flex items-center gap-2">
            <span>✅</span>
            <span>{{ changePasswordSuccess }}</span>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1">Senha Atual <span class="text-red-400">*</span></label>
            <input
              v-model="changePasswordForm.current_password"
              type="password"
              placeholder="••••••••"
              required
              class="w-full h-10 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1">Nova Senha <span class="text-red-400">*</span></label>
            <input
              v-model="changePasswordForm.new_password"
              type="password"
              placeholder="Mínimo de 4 dígitos"
              required
              class="w-full h-10 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1">Confirmar Nova Senha <span class="text-red-400">*</span></label>
            <input
              v-model="changePasswordForm.new_password_confirmation"
              type="password"
              placeholder="Repita a nova senha"
              required
              class="w-full h-10 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div class="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white cursor-pointer"
              @click="showChangePasswordModal = false"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50 flex items-center gap-2"
              :disabled="isChangingPassword"
            >
              <span v-if="isChangingPassword" class="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></span>
              <span>{{ isChangingPassword ? 'Salvando...' : 'Salvar Nova Senha' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- MODAL 6: ESQUECI MINHA SENHA                   -->
    <!-- ============================================== -->
    <div
      v-if="showForgotPasswordModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="showForgotPasswordModal = false"
    >
      <div class="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        <div class="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-xl bg-amber-600/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              ❓
            </div>
            <div>
              <h3 class="text-sm font-bold text-white">Recuperação de Acesso</h3>
              <p class="text-xs text-slate-400">Redefinição de senha do profissional.</p>
            </div>
          </div>
          <button type="button" class="text-slate-400 hover:text-white cursor-pointer" @click="showForgotPasswordModal = false">✕</button>
        </div>

        <form class="p-6 space-y-4" @submit.prevent="submitForgotPassword">
          <div v-if="forgotPasswordError" class="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400 font-medium">
            {{ forgotPasswordError }}
          </div>

          <div v-if="forgotPasswordMessage" class="p-3.5 bg-blue-500/10 border border-blue-500/30 rounded-2xl text-xs text-blue-300 space-y-1 leading-relaxed">
            <div class="font-bold text-blue-400 flex items-center gap-1.5">
              <span>ℹ️</span>
              <span>Instruções</span>
            </div>
            <p>{{ forgotPasswordMessage }}</p>
          </div>

          <div v-if="!forgotPasswordMessage">
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">E-mail ou WhatsApp Cadastrado</label>
            <input
              v-model="forgotPasswordLogin"
              type="text"
              placeholder="Digite seu e-mail ou WhatsApp"
              required
              class="w-full h-10 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
            <p class="text-[11px] text-slate-400 mt-2 leading-relaxed">
              Caso tenha esquecido sua senha, o gestor da clínica pode redefinir o seu acesso imediatamente pelo painel de Profissionais.
            </p>
          </div>

          <div class="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white cursor-pointer"
              @click="showForgotPasswordModal = false"
            >
              Fechar
            </button>
            <button
              v-if="!forgotPasswordMessage"
              type="submit"
              class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50 flex items-center gap-2"
              :disabled="isSubmittingForgot"
            >
              <span v-if="isSubmittingForgot" class="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></span>
              <span>{{ isSubmittingForgot ? 'Consultando...' : 'Solicitar Redefinição' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
